import { useState, useEffect, useCallback } from 'react'
import { authService } from '@/services/auth'

export interface User {
  id: string
  email: string
  name: string
  organizationId?: string
  is_active?: boolean
  email_verified?: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  })

  useEffect(() => {
    // Check for stored auth data
    const token = localStorage.getItem('authToken')
    const userStr = localStorage.getItem('user')

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr)
        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        })
      } catch (error) {
        // Invalid stored data, clear it
        localStorage.removeItem('authToken')
        localStorage.removeItem('user')
        setAuthState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }
    } else {
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const response = await authService.login({ email, password })
        const user = {
          id: response.user.user_id,
          email: response.user.email,
          name: response.user.full_name,
          organizationId: response.user.organization_id,
          is_active: response.user.is_active,
          email_verified: response.user.email_verified,
        }
        const token = response.tokens.access_token
        localStorage.setItem('authToken', token)
        localStorage.setItem('refreshToken', response.tokens.refresh_token)
        localStorage.setItem('user', JSON.stringify(user))
        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        })
        return { user, token }
      } catch (error) {
        throw error
      }
    },
    []
  )

  const logout = useCallback(async () => {
    try {
      await authService.logout()
    } catch (error) {
      // Even if API call fails, clear local storage
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  }, [])

  const refreshToken = useCallback(async () => {
    const refreshTokenValue = localStorage.getItem('refreshToken')
    if (!refreshTokenValue) {
      logout()
      return
    }

    try {
      const response = await authService.refreshToken(refreshTokenValue)
      const userStr = localStorage.getItem('user')
      if (userStr) {
        const user = JSON.parse(userStr)
        localStorage.setItem('authToken', response.access_token)
        localStorage.setItem('refreshToken', response.refresh_token)
        setAuthState({
          user,
          token: response.access_token,
          isAuthenticated: true,
          isLoading: false,
        })
      }
    } catch (error) {
      logout()
    }
  }, [logout])

  return {
    ...authState,
    login,
    logout,
    refreshToken,
  }
}

